import type { PatientDetail } from '#shared/types';
import { formatMoney, joinList } from '#client/lib/format';

interface BillingLine {
  label: string;
  value: string;
  mono?: boolean;
  wide?: boolean;
}

type BilledPatient = Pick<
  PatientDetail,
  | 'total'
  | 'paid'
  | 'balance'
  | 'discount_amount'
  | 'discount_percentage'
  | 'tax_amount'
  | 'tax_percentage'
  | 'other_charges'
  | 'other_charges_label'
  | 'panels'
>;

/**
 * The stored figure, with what was typed as its caption.
 *
 * Not re-derived from the percentage, and not inferred by subtracting the total
 * from the list price — that is the arithmetic that stopped being true when tax
 * arrived.
 */
export function describeDiscount(
  patient: Pick<PatientDetail, 'discount_amount' | 'discount_percentage'>,
): string {
  if (patient.discount_amount <= 0) return '—';
  const caption = patient.discount_percentage > 0 ? ` (${patient.discount_percentage}%)` : '';
  return `${formatMoney(patient.discount_amount)}${caption}`;
}

export function listBillingLines(patient: BilledPatient): BillingLine[] {
  return [
    { label: 'Total', value: formatMoney(patient.total), mono: true },
    { label: 'Paid', value: formatMoney(patient.paid), mono: true },
    { label: 'Balance', value: formatMoney(patient.balance), mono: true },
    { label: 'Discount', value: describeDiscount(patient), mono: true },
    // Zero lines are omitted rather than shown as Rs 0, matching the slip: a
    // tax row on a bill that was not taxed invites the question.
    ...(patient.tax_amount > 0
      ? [
          {
            label: `Sales tax (${patient.tax_percentage}%)`,
            value: formatMoney(patient.tax_amount),
            mono: true,
          },
        ]
      : []),
    ...(patient.other_charges > 0
      ? [
          {
            label: patient.other_charges_label?.trim() || 'Other charges',
            value: formatMoney(patient.other_charges),
            mono: true,
          },
        ]
      : []),
    {
      label: 'Panels',
      value: joinList(patient.panels.map((panel) => panel.name)),
      wide: true,
    },
  ];
}
